
import Vue from "vue";
import TextInput from "@/components/TextInput.vue";
import { formatDate, parseDate } from "@/helpers/date";

const TEXT_FORMAT = "ddMMyyyy";
const DATE_FORMAT = "yyyy-MM-dd";

export default Vue.extend({
  components: {
    TextInput
  },
  data() {
    return {
      showCalendar: false,
      textValue: null
    };
  },
  props: {
    allowedDates: {
      type: Function,
      default: undefined
    },
    name: {
      type: String,
      default: undefined
    },
    value: {
      type: [String, Date],
      default: undefined
    },
    label: {
      type: String,
      default: ""
    },
    error: {
      type: Boolean,
      default: false
    },
    min: {
      type: [String, Date],
      default: undefined
    },
    max: {
      type: [String, Date],
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: "date"
    },
    activePicker: {
      type: String,
      default: undefined,
      validator: (value: string) => {
        return ["YEAR", "MONTH"].includes(value);
      }
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    noMargin: {
      type: Boolean,
      default: false
    },
    validateOnBlur: {
      type: Boolean,
      default: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    showCalendar(val) {
      // Change the default calendar view if specified.
      // Ugly, but it's how they do it on the Vuetify site - https://vuetifyjs.com/en/components/date-pickers
      if (val && this.activePicker) {
        this.$nextTick(
          () => (this.$refs.picker.activePicker = this.activePicker)
        );
      }
    }
  },
  computed: {
    dateValue: {
      get() {
        return this.value ? formatDate(this.value, DATE_FORMAT) : null;
      },
      set(value: any) {
        // Hide the calendar.
        this.showCalendar = false;

        // Set the text value.
        const date = parseDate(value, DATE_FORMAT);

        if (date) {
          this.textValue = formatDate(date, TEXT_FORMAT);
        }
        this.$emit("input", date);
        this.$emit("rawInput", value);
      }
    },
    inputRules() {
      // Hack to fix validation errors appearing when the text input loses focus to the calendar.
      // To prevent the validators firing when the text input loses focus, we disabled the validation
      // rules while the calendar is visible. It's not pretty but it does the job.
      return this.showCalendar ? [] : this.rules;
    },
    formattedMin() {
      if (this.min instanceof Date) {
        // The vuetify datepicker expects a date in ISO format
        return formatDate(this.min, DATE_FORMAT);
      }

      return this.min;
    },
    formattedMax() {
      if (this.max instanceof Date) {
        // The vuetify datepicker expects a date in ISO format
        return formatDate(this.max, DATE_FORMAT);
      }

      return this.max;
    }
  },
  updated() {
    if (this.value) {
      this.textValue = formatDate(this.value, TEXT_FORMAT);
    }
  },
  mounted() {
    if (this.value) {
      this.textValue = formatDate(this.value, TEXT_FORMAT);
    }
  },
  methods: {
    onTextInput(value: string) {
      // Hide the calendar.
      this.showCalendar = false;

      // Try parse the date entered in the input field.
      if (value.length === TEXT_FORMAT.length) {
        const date = parseDate(value, TEXT_FORMAT);
        this.$emit("input", date);
        this.$emit("rawInput", date);
        return;
      }

      this.dateValue = null;
      this.$emit("rawInput", value);
    },
    validate() {
      (this.$refs.textInput as any).validate();
    },
    onBlur() {
      this.$emit("blur");
    }
  }
});
